import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import customStyles from "../../../../customstyle/customstyle";
import ErrorModal from "../../../../reusableComponents/ErrorModal";
import { country } from "../../../../utils/constants/country";
import SuccessModal from "../../../reusableComponents/SuccessModal";
import AccountPassword from "../AccountPassword";
import "./style.scss";
import { clearPersistedData } from "../../../../store/configureStore";
import { deAuthenticate } from "../../../../store/reducers/authSlice";
import { updateUserDetailsRequest } from "../../../../utils/apis";
import { getUser } from "../../../../store/reducers/userSlice";
import { PURGE_Brand } from "../../../../store/reducers/brandSlice";
import { returnMessages } from "../../../../store/reducers/message";

function AccountDetails({ userDetails }) {
  const idToken = useSelector((state) => state.auth.idToken);
  const selectedBrand = useSelector((state) => state.brand.selectedBrand);
  const dispatch = useDispatch();

  const hasUserDetails = userDetails && Object.keys(userDetails).length > 0;
  const [editMode, setLocalEditMode] = useState(false);

  const options = country.map((item) => ({
    value: item.country,
    label: item.country,
    code: item.code,
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: userDetails?.first_name || "",
      last_name: userDetails?.last_name || "",
      contact: userDetails?.contact || "",
      email: userDetails?.email || "",
      country: userDetails?.country || "",
      city: userDetails?.city || "",
      country_code: userDetails?.country_code || "",
      trader_badge: userDetails?.trader_badge || "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().max(50).required("First Name is required"),
      last_name: Yup.string().max(50).required("Last Name is required"),
      contact: Yup.string()
        .matches(/^\+?\d{7,15}$/, "Contact Number is not valid")
        .required("Contact Number is required"),
      email: Yup.string().email("Invalid email address").required("Email is required"),
      country: Yup.string().required("Country is required"),
      // city: Yup.string().required("City is required"),
      country_code: Yup.string()
        .matches(/^\+?\d{1,6}$/, "Country code is invalid")
        .required("Country code is required"),
    }),
    onSubmit: async (values) => {
      setIsSpinner(true);
      try {
        const response = await updateUserDetailsRequest({ formData: values, idToken });
        if (response?.status === 200 || response?.data?.success) {
          dispatch(returnMessages("Updated successfully. Everything went great. Let's continue.", 200));
          dispatch(getUser({ idToken, selectedBrand }));
          setSuccess(true);
          setLocalEditMode(false);
        } else {
          throw new Error(response?.data?.message || "Failed to update details");
        }
      } catch (err) {
        setError(err.message || "Failed to save details");
      } finally {
        setIsSpinner(false);
      }
    },
  });

  const [isSpinner, setIsSpinner] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Disable logic remains unchanged
  const isFieldPermanentlyDisabled = (fieldName) => !!userDetails?.[fieldName];
  const isFieldDisabled = (fieldName) => {
    if (isFieldPermanentlyDisabled(fieldName)) return true;
    return !editMode && hasUserDetails;
  };

  // Toggle edit mode, reset form if cancelling edit
  const handleEditMode = () => {
    if (editMode) {
      formik.resetForm();
    }
    setLocalEditMode(!editMode);
  };

  // Country select handler
  const handleCountryChange = (selectedOption) => {
    formik.setFieldValue("country", selectedOption.value);
    formik.setFieldValue("country_code", selectedOption.code);
  };

  return (
    <>
      <div className="account_details_upper">
        <div
          className="account_details_upper_left"
          style={{ width: "100%" }}
        >
          <div className="account_img_wrapper">
            <p className="accountdetails-image_dp">{`${formik.values.first_name?.charAt(0)?.toUpperCase() || ""} ${formik.values.last_name?.charAt(0)?.toUpperCase() || ""}` || "NA"}</p>
          </div>
          <div className="account_details_upper_content">
            <h4>{`${formik.values.first_name} ${formik.values.last_name}` || "NA"}</h4>
            <p>{formik.values.trader_badge || "N/A"}</p>
          </div>
        </div>
        <div className="account_buttons">
          {hasUserDetails && (
            <>
              {!editMode && (
                <button
                  className="edit_button"
                  onClick={handleEditMode}
                >
                  Edit
                </button>
              )}
              {editMode && (
                <>
                  <button
                    className="save_button"
                    onClick={formik.handleSubmit}
                    disabled={isSpinner || !formik.isValid}
                  >
                    {isSpinner ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="edit_button"
                    onClick={handleEditMode}
                  >
                    Cancel
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="profileFormWrapper">
        <div className="account_details">
          {error && (
            <ErrorModal
              desc={error}
              setModalOpen={setError}
              btnText="Cancel"
            />
          )}
          <div className="account_details_lower">
            <div className="account_form">
              <form onSubmit={formik.handleSubmit}>
                <div className="account_input_container_upper">
                  <div className="account_input">
                    <label
                      htmlFor="firstName"
                      className="label_black"
                    >
                      First Name
                    </label>
                    <div className={`account_input_wrapper ${!isFieldDisabled("first_name") && "editable"}`}>
                      <input
                        className={`account_details_input ${formik.touched.first_name && formik.errors.first_name ? "input_error" : ""}`}
                        type="text"
                        id="firstName"
                        name="first_name"
                        placeholder="First Name"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.first_name}
                        disabled={isFieldDisabled("first_name")}
                      />
                      {formik.touched.first_name && formik.errors.first_name ? <div className="error_message">{formik.errors.first_name}</div> : null}
                    </div>
                  </div>
                  <div className="account_input">
                    <label
                      htmlFor="lastName"
                      className="label_black"
                    >
                      Last Name
                    </label>
                    <div className={`account_input_wrapper ${!isFieldDisabled("last_name") && "editable"}`}>
                      <input
                        className={`account_details_input ${formik.touched.last_name && formik.errors.last_name ? "input_error" : ""}`}
                        type="text"
                        placeholder="Last Name"
                        id="lastName"
                        name="last_name"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.last_name}
                        disabled={isFieldDisabled("last_name")}
                      />
                      {formik.touched.last_name && formik.errors.last_name ? <div className="error_message">{formik.errors.last_name}</div> : null}
                    </div>
                  </div>
                  {/* Contact Number input with separate country code and contact number */}
                  <div className="account_input">
                    <label
                      htmlFor="contact"
                      className="label_black"
                    >
                      Contact Number
                    </label>
                    <div className={`contactNumber_div ${!isFieldDisabled("contact") && "editable"}`}>
                      <div className="countryCodeDiv">
                        {!formik.values.country_code?.startsWith("+") && <span className="contactNumber_p">+</span>}
                        <input
                          className={`contactNumber_input ${formik.touched.country_code && formik.errors.country_code ? "input_error" : ""}`}
                          type="text"
                          id="countryCode"
                          name="country_code"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\+?\d*$/.test(value) && value.replace("+", "").length <= 6) {
                              formik.setFieldValue("country_code", value);
                            }
                          }}
                          onBlur={formik.handleBlur}
                          value={formik.values.country_code || ""}
                          disabled={isFieldDisabled("country_code")}
                        />
                      </div>

                      <div className="account_input_wrapper_contact">
                        <input
                          className={`account_details_input ${formik.touched.contact && formik.errors.contact ? "input_error" : ""}`}
                          type="text"
                          id="contact"
                          name="contact"
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 15) {
                              formik.setFieldValue("contact", value);
                            }
                          }}
                          onBlur={formik.handleBlur}
                          value={formik.values.contact}
                          disabled={isFieldDisabled("contact")}
                        />
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "end" }}>
                          {formik.touched.country_code && formik.errors.country_code ? <div className="error_message">{formik.errors.country_code}</div> : null}{" "}
                          {formik.touched.contact && formik.errors.contact ? <div className="error_message">{formik.errors.contact}</div> : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email, Country with Select and City inputs similarly updated with Formik */}
                <div className="account_input_container_lower">
                  <div className="account_input">
                    <label
                      htmlFor="email"
                      className="label_black"
                    >
                      Email
                    </label>
                    <div className={`account_input_wrapper ${!isFieldDisabled("email") && "editable"}`}>
                      <input
                        className={`account_details_input ${formik.touched.email && formik.errors.email ? "input_error" : ""}`}
                        type="text"
                        id="email"
                        name="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        disabled={isFieldDisabled("email")}
                        style={{ marginRight: "10px" }}
                      />
                      {formik.touched.email && formik.errors.email ? <div className="error_message">{formik.errors.email}</div> : null}
                    </div>
                  </div>
                  <div className="account_input country_selector">
                    <label
                      htmlFor="country"
                      className="label_black"
                    >
                      Country
                    </label>
                    <div className={`account_input_wrapper country ${!isFieldDisabled("country") && "editable"}`}>
                      <Select
                        isSearchable={true}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={options}
                        styles={customStyles}
                        value={formik.values.country ? options.find((opt) => opt.value === formik.values.country) : null}
                        onChange={(selected) => handleCountryChange(selected)}
                        components={{ IndicatorSeparator: () => null }}
                        name="country"
                        id="country"
                        isDisabled={isFieldDisabled("country")}
                        onBlur={() => formik.setFieldTouched("country", true)}
                      />
                      {formik.touched.country && formik.errors.country ? <div className="error_message">{formik.errors.country}</div> : null}
                    </div>
                  </div>
                  <div className="account_input">
                    <label
                      htmlFor="city"
                      className="label_black"
                    >
                      City
                    </label>
                    <div className={`account_input_wrapper ${!isFieldDisabled("city") && "editable"}`}>
                      <input
                        className={`account_details_input ${formik.touched.city && formik.errors.city ? "input_error" : ""}`}
                        type="text"
                        id="city"
                        name="city"
                        placeholder="City"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.city}
                        disabled={isFieldDisabled("city")}
                      />
                      {formik.touched.city && formik.errors.city ? <div className="error_message">{formik.errors.city}</div> : null}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="profileline"></div>
        <AccountPassword
          handleLogout={() => {
            dispatch(deAuthenticate());
            dispatch(PURGE_Brand());
            clearPersistedData();
            window.location.reload();
          }}
        />
      </div>
    </>
  );
}

export default AccountDetails;
